import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type ClosedReason = "deadline_passed" | "late_window_closed" | "already_submitted";

type AssignmentPayload = {
  canSubmit: boolean;
  dueAt: string | Date | null;
  allowLate?: boolean;
  isLate?: boolean;
  lateUntil?: string | Date | null;
  closedReason?: ClosedReason | null;
  submission: { fileName: string; submittedAt: string | Date } | null;
};

const ERROR_OPTIONS = { namespace: "homework.errors" } as const;

type HomeworkPlayerProps = {
  lessonId: string;
};

/**
 * LRN-9: the student always sees whether homework can still be submitted and, if not, why —
 * including the real cutoff for late work. LRN-20: server errors are translated (C4), never the
 * submit button's label or raw English.
 */
export function HomeworkPlayer({ lessonId }: HomeworkPlayerProps) {
  const t = useTranslations("homework");
  const tRoot = useTranslations();
  const locale = useLocale();
  const [assignment, setAssignment] = useState<AssignmentPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const formatDate = (value: string | Date) =>
    new Date(value).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });

  useEffect(() => {
    let active = true;
    // Aborts in flight when the student leaves; see ISSUES_LOG 256.
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    setError(null);
    setSelectedFile(null);

    void (async () => {
      try {
        const res = await apiFetch(`/v1/lessons/${lessonId}/assignment`, {
          signal: controller.signal,
        });
        if (!res.ok) throw await readApiErrorResponse(res);
        const json = (await res.json()) as { data: AssignmentPayload };
        if (!active) return;
        setAssignment(json.data);
      } catch (err) {
        if (!active || (err as Error)?.name === "AbortError") return;
        setLoadError(apiErrorText(err, tRoot, "homework.unavailable", ERROR_OPTIONS));
        setAssignment(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [lessonId, tRoot]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedFile) {
      setError(t("chooseFile"));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", selectedFile);

      const res = await apiFetch(`/v1/lessons/${lessonId}/assignment/submit`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      const result = ((await res.json()) as { data: { fileName: string; submittedAt: string } })
        .data;
      setAssignment((current) =>
        current
          ? {
              ...current,
              canSubmit: false,
              closedReason: "already_submitted",
              submission: { fileName: result.fileName, submittedAt: result.submittedAt },
            }
          : current,
      );
      setSelectedFile(null);
    } catch (err) {
      setError(apiErrorText(err, tRoot, "homework.submitFailed", ERROR_OPTIONS));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>;
  }

  if (loadError) {
    return (
      <p role="alert" className="text-sm text-[var(--danger)]" data-testid="homework-error">
        {loadError}
      </p>
    );
  }

  if (!assignment) {
    return <p className="text-sm text-[var(--text-muted)]">{t("unavailable")}</p>;
  }

  if (assignment.submission) {
    return (
      <section data-testid="homework-result">
        <h2 className="text-lg font-medium">{t("submitted")}</h2>
        <p className="mt-2 text-[var(--text-secondary)]">
          {t("fileLabel")}{" "}
          <span data-testid="homework-file-name">{assignment.submission.fileName}</span>
        </p>
        <p className="mt-1 text-sm text-[var(--text-muted)]" data-testid="homework-submitted-at">
          {t("submittedAt", { date: formatDate(assignment.submission.submittedAt) })}
        </p>
      </section>
    );
  }

  const closedReason = assignment.closedReason ?? null;
  const lateOpen = !closedReason && Boolean(assignment.isLate) && assignment.canSubmit;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="homework-player">
      {assignment.dueAt ? (
        <p className="text-sm text-[var(--text-muted)]" data-testid="homework-due">
          {t("due", { date: formatDate(assignment.dueAt) })}
        </p>
      ) : null}
      {closedReason === "deadline_passed" ? (
        <p
          role="status"
          className="text-sm text-[var(--danger)]"
          data-testid="homework-deadline-closed"
        >
          {t("deadlinePassed")}
        </p>
      ) : null}
      {closedReason === "late_window_closed" && assignment.lateUntil ? (
        <p
          role="status"
          className="text-sm text-[var(--danger)]"
          data-testid="homework-late-closed"
        >
          {t("lateWindowClosed", { date: formatDate(assignment.lateUntil) })}
        </p>
      ) : null}
      {lateOpen ? (
        <p className="text-sm text-[var(--text-heading)]" data-testid="homework-late-open">
          {assignment.lateUntil
            ? t("lateOpenUntil", { date: formatDate(assignment.lateUntil) })
            : t("lateStillOpen")}
        </p>
      ) : null}
      <label className="block text-sm">
        <span className="font-medium">{t("uploadTitle")}</span>
        <input
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
          className="mt-2 block w-full text-sm"
          data-testid="homework-file-input"
          disabled={!assignment.canSubmit}
          onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
        />
      </label>
      {error ? (
        <p role="alert" className="text-sm text-[var(--danger)]" data-testid="homework-error">
          {error}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={submitting || !assignment.canSubmit}
        className="rounded bg-[var(--ink-9)] px-4 py-2 text-sm text-white disabled:opacity-50"
        data-testid="homework-submit"
      >
        {submitting ? t("uploading") : t("submit")}
      </button>
    </form>
  );
}
